import {
  AccessTime as RecentlyOfflineIcon,
  Dangerous as DisableIcon,
  BatteryAlert as BatteryAlertIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Devices as DevicesIcon,
  Search as SearchIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  PhoneIphone as DeviceIcon,
  Pin as PinIcon,
  Refresh as RefreshIcon,
  RemoveCircle as RemoveCircleIcon,
  SignalWifi4Bar as OnlineIcon,
  SignalWifiOff as OfflineIcon,
  Terminal as LogsIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TableCell,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import capitalize from "capitalize";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  AdminEmptyState,
  AdminDialog,
  AdminFilterPanel,
  PageShell,
  AdminTableActions,
  AdminTablePanel,
} from "@/components/common";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingRing } from "@/components/common/LoadingRing";
import { DeviceBatteryChip } from "@/components/common/DeviceBatteryChip";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { useLocalizedTablePagination } from "@/hooks/useLocalizedTablePagination";
import { useTableSorting } from "@/hooks/useTableSorting";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import { buildDeviceLogsUrl, Device, DeviceService } from "@/services/features/DeviceService";
import { ChangingRoomGroup, Store, StoreService } from "@/services/features/StoreService";
import { getDeviceBatteryDisplay } from "@/utils/deviceBattery";
import { formatPercentFromWhole } from "@/utils/number";
import { formatStoreSelectorLabel } from "@/utils/store";
import { formatRelativeStoreTime, getBrowserTimezone } from "@/utils/storeTime";

interface Room {
  id: number;
  name: string;
  groupName: string;
}

interface DeviceFilters {
  showOfflineOnly: boolean;
  hideOldDevices: boolean;
  includeDisabled: boolean;
  deviceType: string;
}

type ChipTone = "default" | "primary" | "secondary" | "error" | "warning" | "success" | "info";
type DeviceSortColumn = "id" | "type" | "status" | "battery" | "app" | "os" | "room" | "asam";

const deviceTypeIconSources: Record<string, string> = {
  ASSOCIATE: "/images/app-icons/associate.svg",
  DOOR: "/images/app-icons/door_central.svg",
  CENTRAL: "/images/app-icons/door_central.svg",
  SHOPPER: "/images/app-icons/shopper_assist.svg",
  ASSIST: "/images/app-icons/shopper_assist.svg",
  SURFACE: "/images/app-icons/surface.svg",
};

const Devices: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const browserTimezone = getBrowserTimezone();
  const locale = i18n.resolvedLanguage || i18n.language || "en-US";
  const roomIdParam = searchParams.get("roomId");
  const roomId = roomIdParam && /^\d+$/.test(roomIdParam) ? Number(roomIdParam) : null;
  const deviceService = DeviceService.getInstance();
  const customerService = CustomerService.getInstance();
  const storeService = StoreService.getInstance();

  // State
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [deviceTypes, setDeviceTypes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [currentPin, setCurrentPin] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Filters
  const [filters, setFilters] = useState<DeviceFilters>({
    showOfflineOnly: searchParams.get("offlineOnly") === "true",
    hideOldDevices: searchParams.get("hideOldDevices") !== "false",
    includeDisabled: searchParams.get("includeDisabled") === "true",
    deviceType: searchParams.get("deviceType") || "",
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const localizedTablePagination = useLocalizedTablePagination();

  const getChipSx = (tone: ChipTone) => {
    if (tone === "default") {
      return {
        bgcolor: "action.hover",
        borderColor: "grey.500",
        color: "grey.500",
        "& .MuiChip-icon": { color: "grey.500" },
      };
    }

    const paletteColor = theme.palette[tone];
    return {
      bgcolor: alpha(paletteColor.main, theme.palette.mode === "dark" ? 0.24 : 0.1),
      borderColor: paletteColor.main,
      color: paletteColor.main,
      "& .MuiChip-icon": { color: paletteColor.main },
    };
  };

  const renderDeviceType = (type: string) => {
    const iconSource = deviceTypeIconSources[type.toUpperCase()];

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {iconSource && (
          <Box
            component="img"
            src={iconSource}
            alt=""
            aria-hidden
            sx={{
              display: "block",
              width: 20,
              height: 20,
              aspectRatio: "1 / 1",
              flexShrink: 0,
              borderRadius: "50%",
              objectFit: "cover",
              objectPosition: "center",
              border: "1px solid",
              borderColor: "grey.400",
              overflow: "hidden",
            }}
          />
        )}
        <Typography variant="body2">{capitalize(type.toLowerCase())}</Typography>
      </Stack>
    );
  };

  const getPlatformIconSource = (operatingSystemType?: string) => {
    switch (operatingSystemType?.toUpperCase()) {
      case "ANDROID":
        return "/images/icons/android_icon.svg";
      case "IOS":
        return "/images/icons/ios_icon.svg";
      default:
        return null;
    }
  };

  const renderOperatingSystemVersion = (device: Device) => {
    const platformIconSource = getPlatformIconSource(device.operatingSystemType);
    const version = device.deviceVersion || t("management.devices.devices.empty.notAvailable");

    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        {platformIconSource && (
          <Box
            component="img"
            src={platformIconSource}
            alt=""
            aria-hidden
            sx={{ width: 18, height: 18, objectFit: "contain", flexShrink: 0 }}
          />
        )}
        <Typography variant="body2">{version}</Typography>
      </Stack>
    );
  };

  const renderAppVersion = (appVersion?: string) => {
    if (!appVersion) {
      return t("management.devices.devices.empty.notAvailable");
    }

    const match = appVersion.match(/^(.*?)(?:\s*\(([^()]*)\))\s*$/);
    const version = (match?.[1].trim() || appVersion).replace(/\s*-\s*$/, "");
    const buildNumber = match?.[2].trim();

    return (
      <Stack spacing={0.25}>
        <Typography variant="body2">{version}</Typography>
        {buildNumber && (
          <Typography
            variant="caption"
            sx={{ color: theme.palette.mode === "dark" ? "grey.300" : "grey.700" }}
          >
            {buildNumber}
          </Typography>
        )}
      </Stack>
    );
  };

  // Load initial data
  useEffect(() => {
    document.title = t("management.devices.devices.documentTitle");
    void loadCustomers();
  }, [t]);

  // Load stores when customer changes
  useEffect(() => {
    if (selectedCustomer) {
      void loadStores(selectedCustomer.id);
    } else {
      setStores([]);
      setSelectedStore(null);
    }
  }, [selectedCustomer]);

  // Load devices when store or filters change
  useEffect(() => {
    if (selectedStore) {
      void loadDevices();
    } else {
      setDevices([]);
    }
  }, [selectedStore, filters, roomId]);

  // Load rooms when store changes
  useEffect(() => {
    if (selectedStore) {
      void loadRooms();
    } else {
      setRooms([]);
    }
  }, [selectedStore]);

  // Initialize from URL params
  useEffect(() => {
    const customerId = searchParams.get("customerId");
    if (customerId && customers.length > 0) {
      const customer = customers.find((c) => c.id === parseInt(customerId));
      if (customer) {
        setSelectedCustomer(customer);
      }
    }
  }, [customers, searchParams]);

  useEffect(() => {
    const storeId = searchParams.get("storeId");
    if (storeId && stores.length > 0) {
      const store = stores.find((s) => s.id === parseInt(storeId));
      if (store) {
        setSelectedStore(store);
      }
    }
  }, [stores, searchParams]);

  const loadCustomers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await customerService.getAllCustomers();
      if (response.success && response.data) {
        setCustomers(
          response.data.map((customer) => ({
            ...customer,
            name: capitalize.words(customer.name),
          })),
        );
      } else {
        setError(t("management.devices.devices.errors.failedLoadCustomers"));
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("management.devices.devices.errors.errorLoadingCustomers"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadStores = async (customerId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await storeService.getStoresByCustomer(customerId);
      if (response.data) {
        setStores(
          response.data.map((store) => ({
            ...store,
            name: capitalize.words(store.name),
          })),
        );
      } else {
        setError(t("management.devices.devices.errors.failedLoadStores"));
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("management.devices.devices.errors.errorLoadingStores"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadDevices = async () => {
    if (!selectedStore) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await deviceService.getDevicesByStoreId(
        selectedStore.id,
        filters.includeDisabled,
      );
      if (response.success && response.body) {
        const allDevices = response.body;
        setDeviceTypes(Array.from(new Set(allDevices.map((device) => device.type))));
        const filteredDevices = filterDevices(allDevices);
        setDevices(filteredDevices);
        setPage(0);
      } else {
        setError(t("management.devices.devices.errors.failedLoadDevices"));
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("management.devices.devices.errors.failedLoadDevices"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadRooms = async () => {
    if (!selectedStore) return;

    try {
      setIsLoading(true);
      const response = await storeService.getChangingRoomsByStore(selectedStore.id);
      if (response.success && response.data) {
        const roomList: Room[] = [];
        response.data.forEach((group: ChangingRoomGroup) => {
          if (group.changingRooms && group.changingRoomGroup) {
            group.changingRooms.forEach((room) => {
              roomList.push({
                id: room.id ?? 0,
                name: room.name,
                groupName:
                  group.changingRoomGroup?.name ||
                  t("management.devices.devices.empty.unknownGroup"),
              });
            });
          }
        });
        setRooms(roomList);
      }
    } catch {
      setRooms([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterDevices = (deviceList: Device[]): Device[] => {
    return deviceList.filter((device) => {
      const storeTimezone = selectedStore?.timezone || "Etc/UTC";
      const updatedAt = device.updatedAt ? moment(device.updatedAt).tz(storeTimezone) : null;
      const createdAt = device.createdAt ? moment(device.createdAt).tz(storeTimezone) : null;

      let isOld = false;
      let isOffline = false;

      if (!updatedAt) {
        if (!createdAt || moment().tz(storeTimezone).diff(createdAt, "months") >= 1) {
          isOld = true;
        }
        isOffline = true;
      } else if (moment().tz(storeTimezone).diff(updatedAt, "months") >= 1) {
        isOld = true;
        isOffline = true;
      } else if (moment().tz(storeTimezone).diff(updatedAt, "hours") >= 1) {
        isOffline = true;
      }

      if (filters.hideOldDevices && isOld) return false;
      if (filters.showOfflineOnly && !isOffline) return false;
      if (filters.deviceType && device.type !== filters.deviceType) return false;
      if (roomId !== null && device.changingRoomId !== roomId) return false;

      return true;
    });
  };

  const handleCustomerChange = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    setSelectedStore(null);
    setPage(0);
    updateSearchParams({ customerId: customer?.id, storeId: null, roomId: null });
  };

  const handleStoreChange = (store: Store | null) => {
    setSelectedStore(store);
    setPage(0);
    updateSearchParams({ storeId: store?.id, roomId: null });
  };

  const handleFilterChange = (key: keyof DeviceFilters, value: boolean | string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
    updateSearchParams({ [key]: value });
  };

  const updateSearchParams = (updates: Record<string, any>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        newParams.delete(key);
      } else {
        newParams.set(key, value.toString());
      }
    });
    setSearchParams(newParams);
  };
  const handleAction = async (action: string, device: Device) => {
    try {
      setIsLoading(true);
      setError(null);

      switch (action) {
        case "restart":
          await deviceService.restartDeviceById(device.id);
          break;
        case "disable":
          await deviceService.disableDeviceById(device.id);
          await loadDevices();
          break;
        case "lock":
          await deviceService.lockDeviceById(device.id, true);
          break;
        case "unlock":
          await deviceService.lockDeviceById(device.id, false);
          break;
        case "logs":
          window.open(buildDeviceLogsUrl(device.id, device.storeId), "_blank");
          break;
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("management.devices.devices.errors.errorPerformingAction"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinAction = async (device: Device) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await deviceService.getDevicePinById(device.id);
      if (response.success && response.body) {
        setCurrentPin(response.body);
        setPinModalOpen(true);
      } else {
        const errorMsg =
          response.error?.message ||
          t("management.devices.devices.errors.failedGetDevicePinUnavailable");
        setError(errorMsg);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      console.error("Error getting device PIN:", err);
      setError(
        err instanceof Error
          ? t("management.devices.devices.errors.errorGettingDevicePinWithMessage", {
              errMessage: err.message,
            })
          : t("management.devices.devices.errors.errorGettingDevicePin"),
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsLoading(false);
    }
  };

  const canRestart = (device: Device) => {
    return (
      device.type &&
      device.operatingSystemType &&
      (device.type === "DOOR" || device.type === "SHOPPER") &&
      device.operatingSystemType === "IOS"
    );
  };

  const canManageSingleAppMode = (device: Device) =>
    device.type.toUpperCase() !== "ASSOCIATE" &&
    device.operatingSystemType?.toUpperCase() !== "ANDROID";

  const getRoomName = (roomId?: number) => {
    if (!roomId) return t("management.devices.devices.empty.notAvailable");
    const room = rooms.find((r) => r.id === roomId);
    return room
      ? `${room.groupName} - ${room.name}`
      : t("management.devices.devices.empty.notAvailable");
  };

  const searchedDevices = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return devices;
    }

    return devices.filter((device) =>
      [
        device.id,
        device.name,
        device.type,
        device.appVersion,
        device.operatingSystemType,
        device.deviceVersion,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [devices, searchQuery]);

  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting(
    searchedDevices,
    (device, column) =>
      ({
        id: device.id,
        type: device.type,
        status: device.updatedAt ? new Date(device.updatedAt).getTime() : null,
        battery: device.batteryPercent,
        app: device.appVersion,
        os: `${device.operatingSystemType} ${device.deviceVersion}`,
        room: getRoomName(device.changingRoomId),
        asam: device.singleAppModeEnabled,
      })[column],
    "id" as DeviceSortColumn,
  );
  const handleTableSort = (column: DeviceSortColumn) => {
    handleSort(column);
    setPage(0);
  };
  const paginatedDevices = sortedItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderPagination = () => (
    <TablePagination
      component="div"
      count={sortedItems.length}
      page={page}
      onPageChange={(_event, nextPage) => setPage(nextPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
      }}
      rowsPerPageOptions={isMobile ? [] : [25, 50, 100, 250]}
      {...localizedTablePagination}
    />
  );

  const resetFilters = () => {
    // Reset all filters to default values
    const defaultFilters = {
      showOfflineOnly: false,
      hideOldDevices: true,
      includeDisabled: false,
      deviceType: "",
    };

    // Reset all selections
    setFilters(defaultFilters);
    setSelectedCustomer(null);
    setSelectedStore(null);
    setDeviceTypes([]);
    setDevices([]);
    setPage(0);

    // Clear all URL parameters
    setSearchParams(new URLSearchParams());
  };

  // New helper function for status cards
  const getDeviceStats = () => {
    if (!devices.length)
      return {
        total: 0,
        online: 0,
        recentlyOffline: 0,
        offline: 0,
        escalate: 0,
        lowBattery: 0,
      };

    const stats = devices.reduce(
      (acc, device) => {
        const status = getStatusInfo(device);
        const hasLowBattery = device.batteryPercent !== undefined && device.batteryPercent < 20;

        // Start with a basic accumulation
        const newAcc = {
          total: acc.total + 1,
          online: status.key === "online" ? acc.online + 1 : acc.online,
          recentlyOffline:
            status.key === "recentlyOffline" ? acc.recentlyOffline + 1 : acc.recentlyOffline,
          offline: status.key === "offline" ? acc.offline + 1 : acc.offline,
          escalate: status.key === "escalate" ? acc.escalate + 1 : acc.escalate,
          lowBattery: hasLowBattery ? acc.lowBattery + 1 : acc.lowBattery,
        };

        return newAcc;
      },
      {
        total: 0,
        online: 0,
        recentlyOffline: 0,
        offline: 0,
        escalate: 0,
        lowBattery: 0,
      },
    );

    return stats;
  };

  const getStatusInfo = (device: Device) => {
    const now = moment().tz(browserTimezone);
    const updatedAt = device.updatedAt ? moment(device.updatedAt).tz(browserTimezone) : null;

    if (!updatedAt)
      return {
        key: "neverConnected" as const,
        icon: <OfflineIcon />,
        label: t("management.devices.devices.status.neverConnected"),
        color: "error" as const,
        tooltip: t("management.devices.devices.tooltips.deviceNeverConnected"),
      };

    const hoursSinceUpdate = now.diff(updatedAt, "hours");
    const daysSinceUpdate = now.diff(updatedAt, "days");
    const minutesSinceUpdate = now.diff(updatedAt, "minutes");

    if (daysSinceUpdate >= 7)
      return {
        key: "escalate" as const,
        icon: <WarningIcon />,
        label: t("management.devices.devices.status.escalate"),
        color: "secondary" as const,
        tooltip: t("management.devices.devices.tooltips.offlineForDaysRequiresAttention", {
          days: daysSinceUpdate,
        }),
      };

    if (hoursSinceUpdate >= 24)
      return {
        key: "offline" as const,
        icon: <OfflineIcon />,
        label: t("management.devices.devices.status.offline"),
        color: "error" as const,
        tooltip: t("management.devices.devices.tooltips.lastSeen", {
          relativeTime: formatRelativeStoreTime(device.updatedAt!, browserTimezone),
        }),
      };

    if (hoursSinceUpdate >= 1)
      return {
        key: "recentlyOffline" as const,
        icon: <RecentlyOfflineIcon />,
        label: t("management.devices.devices.status.recentlyOffline"),
        color: "warning" as const,
        tooltip: t("management.devices.devices.tooltips.lastSeen", {
          relativeTime: formatRelativeStoreTime(device.updatedAt!, browserTimezone),
        }),
      };

    return {
      key: "online" as const,
      icon: <OnlineIcon />,
      label: t("management.devices.devices.status.online"),
      color: "success" as const,
      tooltip:
        minutesSinceUpdate <= 5
          ? t("management.devices.devices.tooltips.currentlyActive")
          : t("management.devices.devices.tooltips.lastSeenMinutesAgo", {
              minutes: minutesSinceUpdate,
            }),
    };
  };

  const renderMobileView = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {devices.length > 0 ? (
        devices.map((device) => {
          const statusInfo = getStatusInfo(device);
          const batteryLevel = device.batteryPercent;
          const batteryDisplay = getDeviceBatteryDisplay(device);

          return (
            <Card key={device.id}>
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                {/* Header with Device Name, ID, and Status */}
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <DeviceIcon sx={{ color: "text.secondary" }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {device.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        #{device.id}
                      </Typography>
                    </Stack>
                    <Chip
                      icon={statusInfo.icon}
                      label={statusInfo.label}
                      color={statusInfo.key === "neverConnected" ? "default" : statusInfo.color}
                      size="small"
                      variant="outlined"
                      sx={{
                        ...getChipSx(
                          statusInfo.key === "neverConnected" ? "default" : statusInfo.color,
                        ),
                        height: 24,
                      }}
                    />
                  </Stack>

                  {/* Device Info Grid */}
                  <Grid container spacing={2} sx={{ pt: 1 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("management.devices.devices.fields.type")}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>{renderDeviceType(device.type)}</Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("management.devices.devices.fields.room")}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {getRoomName(device.changingRoomId)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("management.devices.devices.fields.appVersion")}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>{renderAppVersion(device.appVersion)}</Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("management.devices.devices.fields.osVersion")}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>{renderOperatingSystemVersion(device)}</Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("management.devices.devices.fields.asam")}
                      </Typography>
                      <Chip
                        label={
                          device.singleAppModeEnabled
                            ? t("management.devices.devices.status.enabled")
                            : t("management.devices.devices.status.disabled")
                        }
                        color={device.singleAppModeEnabled ? "success" : "default"}
                        size="small"
                        variant="outlined"
                        sx={{
                          ...getChipSx(device.singleAppModeEnabled ? "success" : "default"),
                          mt: 0.5,
                          height: 24,
                        }}
                      />
                    </Grid>
                    {batteryDisplay && batteryLevel !== undefined && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t("management.devices.devices.fields.battery")}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <DeviceBatteryChip
                            device={device}
                            locale={locale}
                            wallPowerLabel={t("commandCenter.status.wallPower")}
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* Actions */}
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    sx={{
                      mt: 1,
                      gap: 1,
                      "& > button": {
                        flex: "1 1 calc(50% - 4px)", // Take up half the space minus the gap
                        minWidth: "auto",
                        px: 1,
                      },
                    }}
                  >
                    <Button
                      size="small"
                      startIcon={<PinIcon />}
                      onClick={() => void handlePinAction(device)}
                      variant="outlined"
                    >
                      {t("management.devices.devices.actions.pin")}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<LogsIcon />}
                      onClick={() => void handleAction("logs", device)}
                      variant="outlined"
                      color="primary"
                    >
                      {t("management.devices.devices.actions.logs")}
                    </Button>
                    {canManageSingleAppMode(device) && (
                      <Button
                        size="small"
                        startIcon={device.singleAppModeEnabled ? <LockOpenIcon /> : <LockIcon />}
                        onClick={() =>
                          void handleAction(device.singleAppModeEnabled ? "unlock" : "lock", device)
                        }
                        variant="outlined"
                        color={device.singleAppModeEnabled ? "warning" : "success"}
                      >
                        {device.singleAppModeEnabled
                          ? t("management.devices.devices.actions.unlock")
                          : t("management.devices.devices.actions.lock")}
                      </Button>
                    )}
                    {canRestart(device) && (
                      <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={() => void handleAction("restart", device)}
                        variant="outlined"
                        color="primary"
                      >
                        {t("management.devices.devices.tooltips.restartApp")}
                      </Button>
                    )}
                    {device.enabled && (
                      <Button
                        size="small"
                        startIcon={<DisableIcon />}
                        onClick={() => void handleAction("disable", device)}
                        variant="outlined"
                        color="error"
                      >
                        {t("management.devices.devices.actions.disable")}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          );
        })
      ) : (
        <Card>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <AdminEmptyState
              icon={<DevicesIcon sx={{ fontSize: 40 }} />}
              text={
                !selectedCustomer
                  ? t("management.devices.devices.empty.selectCustomer")
                  : !selectedStore
                    ? t("management.devices.devices.empty.selectStore")
                    : t("management.devices.devices.empty.noDevices")
              }
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const renderDesktopView = () => (
    <AdminTablePanel
      header={
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { xs: "stretch", lg: "center" },
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            label={t("management.devices.devices.search.label")}
            placeholder={t("management.devices.devices.search.placeholder")}
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t("common.clear")}
                    onClick={() => {
                      setSearchQuery("");
                      setPage(0);
                    }}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ flex: 1, minWidth: 0 }}
          />
          <Box
            sx={{
              flexShrink: 0,
              "& .MuiTablePagination-root": { p: 0 },
            }}
          >
            {renderPagination()}
          </Box>
        </Box>
      }
      footer={<Box sx={{ display: "flex", justifyContent: "flex-end" }}>{renderPagination()}</Box>}
      containerProps={{ sx: { overflowX: "auto" } }}
      tableSx={{ minWidth: 900 }}
    >
      <TableHead>
        <TableRow>
          <SortableTableCell
            activeColumn={sortColumn}
            column="id"
            direction={sortDirection}
            onSort={handleTableSort}
          >
            {t("management.devices.devices.fields.id")}
          </SortableTableCell>
          {filters.deviceType ? (
            <TableCell>{t("management.devices.devices.fields.name")}</TableCell>
          ) : (
            <SortableTableCell
              activeColumn={sortColumn}
              column="type"
              direction={sortDirection}
              onSort={handleTableSort}
              sx={{ minWidth: 160 }}
            >
              {t("management.devices.devices.fields.type")}
            </SortableTableCell>
          )}
          <SortableTableCell
            activeColumn={sortColumn}
            column="status"
            direction={sortDirection}
            onSort={handleTableSort}
          >
            {t("management.devices.devices.fields.status")}
          </SortableTableCell>
          <SortableTableCell
            activeColumn={sortColumn}
            column="battery"
            direction={sortDirection}
            onSort={handleTableSort}
          >
            {t("management.devices.devices.fields.battery")}
          </SortableTableCell>
          <SortableTableCell
            activeColumn={sortColumn}
            column="app"
            direction={sortDirection}
            onSort={handleTableSort}
          >
            {t("management.devices.devices.fields.app")}
          </SortableTableCell>
          <SortableTableCell
            activeColumn={sortColumn}
            column="os"
            direction={sortDirection}
            onSort={handleTableSort}
          >
            {t("management.devices.devices.fields.os")}
          </SortableTableCell>
          <SortableTableCell
            activeColumn={sortColumn}
            column="room"
            direction={sortDirection}
            onSort={handleTableSort}
          >
            {t("management.devices.devices.fields.room")}
          </SortableTableCell>
          <SortableTableCell
            activeColumn={sortColumn}
            column="asam"
            direction={sortDirection}
            onSort={handleTableSort}
            align="center"
            sx={{ width: 48, minWidth: 48, px: 0.5, whiteSpace: "nowrap", textAlign: "center" }}
          >
            {t("management.devices.devices.fields.asam")}
          </SortableTableCell>
          <TableCell align="right">{t("management.devices.devices.fields.actions")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedItems.length === 0 ? (
          <TableRow>
            <TableCell colSpan={9} align="center">
              <AdminEmptyState
                icon={<DevicesIcon sx={{ fontSize: 40 }} />}
                text={
                  !selectedCustomer
                    ? t("management.devices.devices.empty.selectCustomer")
                    : !selectedStore
                      ? t("management.devices.devices.empty.selectStore")
                      : t("management.devices.devices.empty.noDevices")
                }
              />
            </TableCell>
          </TableRow>
        ) : (
          paginatedDevices.map((device) => {
            const batteryLevel = device.batteryPercent;
            const batteryDisplay = getDeviceBatteryDisplay(device);
            const statusInfo = getStatusInfo(device);
            return (
              <TableRow
                key={device.id}
                hover
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/admin/hardware/devices/history/${device.id}`)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) {
                    return;
                  }

                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(`/admin/hardware/devices/history/${device.id}`);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  "&&:hover, &&:hover > .MuiTableCell-root": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <TableCell>
                  {filters.deviceType ? (
                    <Typography variant="body2">{device.id}</Typography>
                  ) : (
                    <Tooltip title={device.name}>
                      <Typography variant="body2">{device.id}</Typography>
                    </Tooltip>
                  )}
                </TableCell>
                {filters.deviceType ? (
                  <TableCell>
                    <Typography variant="body2">{device.name}</Typography>
                  </TableCell>
                ) : (
                  <TableCell sx={{ minWidth: 160 }}>
                    <Tooltip title={device.name}>
                      <Box>{renderDeviceType(device.type)}</Box>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell>
                  <Tooltip title={statusInfo.tooltip}>
                    <Chip
                      icon={statusInfo.icon}
                      label={statusInfo.label}
                      color={statusInfo.key === "neverConnected" ? "default" : statusInfo.color}
                      size="small"
                      variant="outlined"
                      sx={getChipSx(
                        statusInfo.key === "neverConnected" ? "default" : statusInfo.color,
                      )}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {!batteryDisplay || batteryLevel === undefined ? (
                    t("management.devices.devices.empty.notAvailable")
                  ) : (
                    <Tooltip
                      title={t("management.devices.devices.tooltips.batteryLevel", {
                        batteryLevel: formatPercentFromWhole(batteryLevel, locale),
                      })}
                    >
                      <DeviceBatteryChip
                        device={device}
                        locale={locale}
                        wallPowerLabel={t("commandCenter.status.wallPower")}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{renderAppVersion(device.appVersion)}</TableCell>
                <TableCell>{renderOperatingSystemVersion(device)}</TableCell>
                <TableCell>{getRoomName(device.changingRoomId)}</TableCell>
                <TableCell
                  align="center"
                  sx={{ width: 48, minWidth: 48, px: 0.5, textAlign: "center" }}
                >
                  <Tooltip
                    title={
                      device.singleAppModeEnabled
                        ? t("management.devices.devices.status.enabled")
                        : t("management.devices.devices.status.disabled")
                    }
                  >
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      {device.singleAppModeEnabled ? (
                        <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                      ) : (
                        <RemoveCircleIcon sx={{ color: "grey.500", fontSize: 16 }} />
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <AdminTableActions
                    actions={[
                      {
                        icon: <PinIcon />,
                        hint: t("management.devices.devices.tooltips.viewPin"),
                        action: () => void handlePinAction(device),
                      },
                      {
                        icon: <LogsIcon />,
                        hint: t("management.devices.devices.tooltips.viewLogs"),
                        action: () => void handleAction("logs", device),
                        color: "primary",
                      },
                      ...(canManageSingleAppMode(device)
                        ? [
                            {
                              icon: device.singleAppModeEnabled ? <LockOpenIcon /> : <LockIcon />,
                              hint: device.singleAppModeEnabled
                                ? t("management.devices.devices.tooltips.takeOutOfAsam")
                                : t("management.devices.devices.tooltips.putInAsam"),
                              action: () =>
                                void handleAction(
                                  device.singleAppModeEnabled ? "unlock" : "lock",
                                  device,
                                ),
                              color: device.singleAppModeEnabled
                                ? ("warning" as const)
                                : ("success" as const),
                              dividerBefore: true,
                            },
                          ]
                        : []),
                      ...(canRestart(device)
                        ? [
                            {
                              icon: <RefreshIcon />,
                              hint: t("management.devices.devices.tooltips.restartApp"),
                              action: () => void handleAction("restart", device),
                              dividerBefore: !canManageSingleAppMode(device),
                            },
                          ]
                        : []),
                      ...(device.enabled
                        ? [
                            {
                              icon: <DisableIcon />,
                              hint: t("management.devices.devices.tooltips.disableDevice"),
                              action: () => void handleAction("disable", device),
                              color: "error" as const,
                              dividerBefore: true,
                            },
                          ]
                        : []),
                    ]}
                  />
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </AdminTablePanel>
  );

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.devices.devices.title")}
      icon={<DevicesIcon />}
      titleAccessory={<LoadingRing loading={isLoading} />}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        {/* Customer and Store Selection */}
        <AdminFilterPanel sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* First row of filters */}
            <Grid item xs={12} md>
              <MuiAutocompleteMultiSelect
                label={t("management.devices.devices.fields.customer")}
                options={customers}
                value={selectedCustomer ? [selectedCustomer] : []}
                onChange={(next) => handleCustomerChange(next[0] ?? null)}
                getOptionLabel={(option) => option.name}
                getOptionKey={(option) => option.id}
                loading={isLoading && customers.length === 0}
                disabled={isLoading && customers.length === 0}
              />
            </Grid>

            <Grid item xs={12} md>
              <MuiAutocompleteMultiSelect
                label={t("management.devices.devices.fields.store")}
                options={stores}
                value={selectedStore ? [selectedStore] : []}
                onChange={(next) => handleStoreChange(next[0] ?? null)}
                getOptionLabel={(option) => formatStoreSelectorLabel(option, t, user?.role, "name")}
                getOptionKey={(option) => option.id}
                disabled={!selectedCustomer || (isLoading && stores.length === 0)}
                loading={isLoading && selectedCustomer !== null && stores.length === 0}
              />
            </Grid>

            <Grid item xs={12} md>
              <MuiAutocompleteMultiSelect
                label={t("management.devices.devices.fields.deviceType")}
                options={deviceTypes}
                value={filters.deviceType ? [filters.deviceType] : []}
                onChange={(next) => handleFilterChange("deviceType", next[0] ?? "")}
                getOptionLabel={(option) => option}
                getOptionKey={(option) => option}
                disabled={!selectedStore || (isLoading && deviceTypes.length === 0)}
                loading={isLoading && selectedStore !== null && deviceTypes.length === 0}
              />
            </Grid>

            <Grid item xs={12} md="auto" sx={{ alignSelf: "center" }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={resetFilters}
                disabled={!selectedStore}
                color="primary"
                size="medium"
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                {t("common.clearFilters")}
              </Button>
            </Grid>

            {/* Divider */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Toggle switches */}
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.showOfflineOnly}
                    onChange={(e) => handleFilterChange("showOfflineOnly", e.target.checked)}
                    disabled={!selectedStore}
                  />
                }
                label={t("management.devices.devices.fields.offlineOnly")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.hideOldDevices}
                    onChange={(e) => handleFilterChange("hideOldDevices", e.target.checked)}
                    disabled={!selectedStore}
                  />
                }
                label={t("management.devices.devices.fields.hideOldDevices")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.includeDisabled}
                    onChange={(e) => handleFilterChange("includeDisabled", e.target.checked)}
                    disabled={!selectedStore}
                  />
                }
                label={t("management.devices.devices.fields.includeDisabled")}
              />
            </Grid>
          </Grid>
        </AdminFilterPanel>

        {/* Statistics Cards */}
        {selectedStore && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Total Devices Card */}
            <Grid item xs={12} md={6} lg={2}>
              <Card sx={{ p: 2, height: "100%" }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <DeviceIcon color="primary" />
                  <Box>
                    <Typography variant="h5">{getDeviceStats().total}</Typography>
                    <Typography color="text.secondary" variant="body2">
                      {t("management.devices.devices.text.totalDevices")}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </Grid>

            {/* Online Devices Card */}
            <Grid item xs={12} md={6} lg={2}>
              <Card sx={{ p: 2, height: "100%" }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <OnlineIcon color="success" />
                  <Box>
                    <Typography variant="h5">{getDeviceStats().online}</Typography>
                    <Typography color="text.secondary" variant="body2">
                      {t("management.devices.devices.text.onlineDevices")}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </Grid>

            {/* Recently Offline Card */}
            <Grid item xs={12} md={6} lg={2}>
              <Card sx={{ p: 2, height: "100%" }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <RecentlyOfflineIcon color="warning" />
                  <Box>
                    <Typography variant="h5">{getDeviceStats().recentlyOffline}</Typography>
                    <Typography color="text.secondary" variant="body2">
                      {t("management.devices.devices.text.recentlyOffline")}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </Grid>

            {/* Offline Devices Card */}
            <Grid item xs={12} md={6} lg={2}>
              <Card sx={{ p: 2, height: "100%" }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <OfflineIcon color="error" />
                  <Box>
                    <Typography variant="h5">{getDeviceStats().offline}</Typography>
                    <Typography color="text.secondary" variant="body2">
                      {t("management.devices.devices.text.offlineDevices")}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </Grid>

            {/* Escalate Devices Card */}
            <Grid item xs={12} md={6} lg={2}>
              <Card sx={{ p: 2, height: "100%" }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <WarningIcon color="secondary" />
                  <Box>
                    <Typography variant="h5">{getDeviceStats().escalate}</Typography>
                    <Typography color="text.secondary" variant="body2">
                      {t("management.devices.devices.text.escalateDevices")}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </Grid>

            {/* Low Battery Devices Card */}
            <Grid item xs={12} md={6} lg={2}>
              <Card sx={{ p: 2, height: "100%" }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <BatteryAlertIcon color="warning" />
                  <Box>
                    <Typography variant="h5">{getDeviceStats().lowBattery}</Typography>
                    <Typography color="text.secondary" variant="body2">
                      {t("management.devices.devices.text.lowBattery")}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Devices Table/Cards */}
        <Box>{isMobile ? renderMobileView() : renderDesktopView()}</Box>
      </Box>

      <AdminDialog
        open={pinModalOpen}
        onClose={() => setPinModalOpen(false)}
        maxWidth="sm"
        title={t("management.devices.devices.text.devicePin")}
        icon={<PinIcon />}
      >
        <Typography variant="h4" sx={{ textAlign: "center", fontFamily: "monospace" }}>
          {currentPin}
        </Typography>
      </AdminDialog>
    </PageShell>
  );
};

export default Devices;
